import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { Link, TabOptions, TabOptionsContent, TabOptionsGroup } from '@dndbeyond/character-common-components/es';
import {
    rulesEngineSelectors,
    characterActions,
    AbilityLookup,
    Action,
    ActionUtils,
    CharClass,
    ClassFeature,
    ClassUtils,
    DataOriginRefData,
    Feat,
    FeatureUtils,
    FeatUtils,
    InfusionChoice,
    InfusionChoiceUtils,
    SpellUtils,
    Race,
    RacialTraitUtils,
    Spell,
    RacialTrait,
    SnippetData,
    RuleData,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../Shared/actions';
import { appEnvSelectors } from '../../../Shared/selectors';
import { PaneComponentEnum, PaneIdentifierUtils } from '../../../Shared/utils';
import ClassesDetail from '../../components/ClassesDetail';
import ContentGroup from '../../components/ContentGroup';
import FeatsDetail from '../../components/FeatsDetail';
import RaceDetail from '../../components/RaceDetail';
import { SheetAppState } from '../../typings';

const TAB_KEY = {
    ALL: 'ALL',
    CLASS: 'CLASS',
    RACE: 'RACE',
    FEAT: 'FEAT',
};

interface Props extends DispatchProp {
    feats: Array<Feat>;
    classes: Array<CharClass>;
    race: Race | null;
    snippetData: SnippetData;
    ruleData: RuleData;
    abilityLookup: AbilityLookup;
    dataOriginRefData: DataOriginRefData;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
    racialTraits: Array<RacialTrait>;
}
class Features extends React.PureComponent<Props> {
    handleInfusionChoiceShow = (infusionChoice: InfusionChoice): void => {
        const { dispatch } = this.props;

        const choiceKey = InfusionChoiceUtils.getKey(infusionChoice);
        if (choiceKey !== null) {
            dispatch(
                sidebarActions.paneHistoryStart(
                    PaneComponentEnum.INFUSION_CHOICE,
                    PaneIdentifierUtils.generateInfusionChoice(choiceKey),
                ),
            );
        }
    };

    handleActionUseSet = (action: Action, uses: number): void => {
        const { dispatch } = this.props;
        const id = ActionUtils.getId(action);
        const entityTypeId = ActionUtils.getEntityTypeId(action);
        const dataOriginType = ActionUtils.getDataOriginType(action);

        if (id !== null && entityTypeId !== null) {
            dispatch(characterActions.actionUseSet(id, entityTypeId, uses, dataOriginType));
        }
    };

    handleSpellUseSet = (spell: Spell, uses: number): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        const mappingEntityTypeId = SpellUtils.getMappingEntityTypeId(spell);
        const dataOriginType = SpellUtils.getDataOriginType(spell);

        if (mappingId !== null && mappingEntityTypeId !== null && dataOriginType !== null) {
            dispatch(characterActions.spellUseSet(mappingId, mappingEntityTypeId, uses, dataOriginType));
        }
    };

    handleSpellDetailShow = (spell: Spell): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        if (mappingId !== null) {
            dispatch(
                sidebarActions.paneHistoryStart(
                    PaneComponentEnum.CHARACTER_SPELL_DETAIL,
                    PaneIdentifierUtils.generateCharacterSpell(mappingId),
                ),
            );
        }
    };

    handleClassFeatureShow = (feature: ClassFeature, charClass: CharClass): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.CLASS_FEATURE_DETAIL,
                PaneIdentifierUtils.generateClassFeature(
                    FeatureUtils.getId(feature),
                    ClassUtils.getMappingId(charClass),
                ),
            ),
        );
    };

    handleRacialTraitShow = (racialTrait: RacialTrait): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.RACIAL_TRAIT_DETAIL,
                PaneIdentifierUtils.generateRacialTrait(RacialTraitUtils.getId(racialTrait)),
            ),
        );
    };

    handleFeatShow = (feat: Feat): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryStart(
                PaneComponentEnum.FEAT_DETAIL,
                PaneIdentifierUtils.generateFeat(FeatUtils.getId(feat)),
            ),
        );
    };

    handleFeatsManageShow = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.FEATS_MANAGE));
    };

    handleActionShow = (action: Action): void => {
        const { dispatch } = this.props;

        const mappingId = ActionUtils.getMappingId(action);
        const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);

        if (mappingId !== null && mappingEntityTypeId !== null) {
            dispatch(
                sidebarActions.paneHistoryStart(
                    PaneComponentEnum.ACTION,
                    PaneIdentifierUtils.generateAction(mappingId, mappingEntityTypeId),
                ),
            );
        }
    };

    renderClassContent = (): React.ReactNode => {
        const {
            classes,
            feats,
            isReadonly,
            snippetData,
            ruleData,
            abilityLookup,
            dataOriginRefData,
            proficiencyBonus,
            theme,
        } = this.props;

        return (
            <ContentGroup header="Class Features" theme={theme}>
                <ClassesDetail
                    classes={classes}
                    onActionUseSet={this.handleActionUseSet}
                    onActionClick={this.handleActionShow}
                    onSpellClick={this.handleSpellDetailShow}
                    onSpellUseSet={this.handleSpellUseSet}
                    onFeatureClick={this.handleClassFeatureShow}
                    onInfusionChoiceClick={this.handleInfusionChoiceShow}
                    feats={feats}
                    isReadonly={isReadonly}
                    snippetData={snippetData}
                    ruleData={ruleData}
                    abilityLookup={abilityLookup}
                    dataOriginRefData={dataOriginRefData}
                    proficiencyBonus={proficiencyBonus}
                    theme={theme}
                />
            </ContentGroup>
        );
    };

    renderRaceContent = (): React.ReactNode => {
        const {
            feats,
            isReadonly,
            snippetData,
            ruleData,
            abilityLookup,
            dataOriginRefData,
            proficiencyBonus,
            theme,
            racialTraits,
        } = this.props;

        return (
            <ContentGroup header="Racial Traits" theme={theme}>
                <RaceDetail
                    onActionUseSet={this.handleActionUseSet}
                    onActionClick={this.handleActionShow}
                    onSpellClick={this.handleSpellDetailShow}
                    onSpellUseSet={this.handleSpellUseSet}
                    onFeatureClick={this.handleRacialTraitShow}
                    feats={feats}
                    isReadonly={isReadonly}
                    snippetData={snippetData}
                    ruleData={ruleData}
                    abilityLookup={abilityLookup}
                    dataOriginRefData={dataOriginRefData}
                    proficiencyBonus={proficiencyBonus}
                    theme={theme}
                    racialTraits={racialTraits}
                />
            </ContentGroup>
        );
    };

    renderFeatContent = (): React.ReactNode => {
        const { feats, isReadonly, snippetData, ruleData, abilityLookup, dataOriginRefData, proficiencyBonus, theme } =
            this.props;

        return (
            <ContentGroup header="Feats" theme={theme}>
                {!isReadonly && (
                    <div className="ct-features__management-link">
                        <Link useTheme={true} onClick={this.handleFeatsManageShow} role="button">
                            Manage Feats
                        </Link>
                    </div>
                )}
                <FeatsDetail
                    feats={feats}
                    onActionUseSet={this.handleActionUseSet}
                    onActionClick={this.handleActionShow}
                    onSpellClick={this.handleSpellDetailShow}
                    onSpellUseSet={this.handleSpellUseSet}
                    onFeatureClick={this.handleFeatShow}
                    isReadonly={isReadonly}
                    snippetData={snippetData}
                    ruleData={ruleData}
                    abilityLookup={abilityLookup}
                    dataOriginRefData={dataOriginRefData}
                    proficiencyBonus={proficiencyBonus}
                    theme={theme}
                />
            </ContentGroup>
        );
    };

    render() {
        return (
            <div className="ct-features">
                <TabOptions hideBorder={true} initialActiveKey={TAB_KEY.ALL} layoutType="pill">
                    <TabOptionsGroup tabKey={TAB_KEY.ALL} label="All">
                        <TabOptionsContent>
                            {this.renderClassContent()}
                            {this.renderRaceContent()}
                            {this.renderFeatContent()}
                        </TabOptionsContent>
                    </TabOptionsGroup>
                    <TabOptionsGroup tabKey={TAB_KEY.CLASS} label="Class Features">
                        <TabOptionsContent>{this.renderClassContent()}</TabOptionsContent>
                    </TabOptionsGroup>
                    <TabOptionsGroup tabKey={TAB_KEY.RACE} label="Racial Traits">
                        <TabOptionsContent>{this.renderRaceContent()}</TabOptionsContent>
                    </TabOptionsGroup>
                    <TabOptionsGroup tabKey={TAB_KEY.FEAT} label="Feats">
                        <TabOptionsContent>{this.renderFeatContent()}</TabOptionsContent>
                    </TabOptionsGroup>
                </TabOptions>
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        classes: rulesEngineSelectors.getClasses(state),
        feats: rulesEngineSelectors.getBaseFeats(state),
        race: rulesEngineSelectors.getRace(state),
        snippetData: rulesEngineSelectors.getSnippetData(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        dataOriginRefData: rulesEngineSelectors.getDataOriginRefData(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        racialTraits: rulesEngineSelectors.getCurrentLevelRacialTraits(state),
    };
}
export default connect(mapStateToProps)(Features);
